import React from 'react';
import Icon from './icons';
import MasterPanel from './MasterPanel';
import GenDocRow from './GenDocRow';
import VicsLogin from './VicsLogin';
import { MasterCollapse, MasterChip, getS3, getUp, getUpKey, OkFile, UpBtn, RefFile, RefDoc, Wait, S3Row, BulkSaveBtn, s3UploadHandler, dispName } from './stepcommon';
import { getVers, RELS } from './gendocs';
import { collectGenFiles, openS3, downloadCollectFile, downloadUploadFile, fetchCollectBlob, saveS3ToProject } from './docgen';
import { mval } from './collect';
import { DEFAULT_AGENT } from './types';
import type { Project, MasterAgent } from './types';

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  masterOpen: boolean;
  onToggleMaster: () => void;
  onPv: (k: string, idx?: number) => void;
  onVer: (k: string) => void;
  onFilePv: (name: string, title?: string) => void;
}

export default function Step4({ p, onPatch, toast, masterOpen, onToggleMaster, onPv, onVer, onFilePv }: Props) {
  const collect = p.collect;
  const upApproval = s3UploadHandler(p, 's4', 'approval', onPatch, toast, '조합결성계획 승인 공문');
  const gd = (k: string) => <GenDocRow p={p} k={k} seal={false} onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} />;
  const ref3 = (src: string) => {
    const cur = getVers(p, src).slice(-1)[0];
    return <RefDoc pdf={cur.pdf} tag="3단계 최종본" onPv={() => onPv(src)} onDl={() => { const v = getVers(p, src).slice(-1)[0]; if (v.s3pdf) void saveS3ToProject(p, v.s3pdf, v.pdf || src + '.pdf', toast); else toast('PDF가 아직 생성되지 않았습니다 — 3단계에서 생성하세요'); }} toast={toast} />;
  };
  const gpidFn = collect ? mval(collect.gp, 'idCard') : '';
  const approvalKey = getUpKey(p, 's4', 'approval');
  const approvalCur = getUp(p, 's4', 'approval');
  const approvalDisp = approvalCur ? dispName(approvalCur, '조합결성계획 승인 공문', p.org || '') : approvalCur;
  const bulkFiles = [
    ...collectGenFiles(p, ['rule', 'plan', 'roster', 'lease', 'bizreg']),
    // 화면 표시 행 중 누락분 보완: 승인 공문(업로드) + GP 신분증(1단계 연동)
    ...(approvalCur ? (approvalKey ? [{ key: approvalKey, name: approvalDisp }] : [{ key: '', name: approvalCur, getBlob: () => fetchCollectBlob(p, approvalCur) }]) : []),
    ...(gpidFn ? [{ key: '', name: gpidFn, getBlob: () => fetchCollectBlob(p, gpidFn) }] : []),
  ];

  return (
    <div className="sact">
      <div className="sact-h" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>📑 고유번호증 발급 · 구비서류</span>
        <BulkSaveBtn files={bulkFiles} toast={toast} pid={p.id} />
      </div>
      <MasterCollapse open={masterOpen} onToggle={onToggleMaster} chip={<MasterChip m={p.master} s4 />} title="📋 공통정보 (마스터) · 3단계 연동 + 4단계" sub="열기를 누르면 펼쳐집니다 · 3단계 입력 그대로 연동">
        <MasterPanel
          p={p} onPatch={onPatch} toast={toast} withAnswers={false}
          regenKeys={['roster', 'lease', 'bizreg']}
          regenLabel="마스터값으로 4단계 문서 재생성"
          regenToast="마스터값으로 4단계 문서(명부·임대차·사업자등록) 재생성됨"
          extra={({ m, editM, onDocView }) => (
            <>
              <div className="mgrp">4단계 · 조합 주소 / 임대차 / 개업</div>
              <div className="mgrid">
                <div className="mf"><label>조합 주소 소유주</label>
                  <div className="mfv">
                    <input value={m.s4.owner || ''} onChange={(e) => editM((mm) => { mm.s4.owner = e.target.value; if (mm.s4.owners && mm.s4.owners.length) mm.s4.owners[0].name = e.target.value; })} />
                    <button className="vbtn" onClick={() => onDocView({ free: true, docKey: '조합 주소지 등기부등본', label: '조합 주소 소유주', s4field: 'owner', fileName: getS3(p).up.reg || '', s3key: getS3(p).upKey?.reg || '' })}>📄 등본 열람</button>
                  </div>
                </div>
                <div className="mf"><label>소유주 주민등록번호</label><input value={m.s4.ownerRrn || ''} onChange={(e) => editM((mm) => { mm.s4.ownerRrn = e.target.value; if (mm.s4.owners && mm.s4.owners.length) mm.s4.owners[0].rrn = e.target.value; })} /></div>
                <div className="mf"><label>소유주 휴대폰번호 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· 임대차 서명란 표기</span></label><input value={m.s4.ownerTel || ''} onChange={(e) => editM((mm) => { mm.s4.ownerTel = e.target.value; if (mm.s4.owners && mm.s4.owners.length) mm.s4.owners[0].tel = e.target.value; })} placeholder="010-0000-0000" /></div>
                <div className="mf"><label>소유주 집주소 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· 임대차계약서 임대인 주소</span></label>
                  <div className="mfv">
                    <input value={m.s4.ownerAddr || ''} onChange={(e) => editM((mm) => { mm.s4.ownerAddr = e.target.value; if (mm.s4.owners && mm.s4.owners.length) mm.s4.owners[0].addr = e.target.value; })} placeholder="미입력 시 GP 집주소 사용" />
                    <button className="vbtn" onClick={() => onDocView({ free: true, docKey: '조합 주소지 등기부등본', label: '소유주 집주소', s4field: 'ownerAddr', fileName: getS3(p).up.reg || '', s3key: getS3(p).upKey?.reg || '' })}>📄 등본 열람</button>
                  </div>
                </div>
                {/* 임대인 복수(공동 소유) — 위 소유주가 임대인 1, 아래에 칸 자체를 추가 (사용자 확정 2026-07-17) */}
                {(m.s4.owners || []).slice(1).map((o, oi) => (
                  <div className="mf" key={oi} style={{ gridColumn: '1 / -1' }}>
                    <label>임대인 {oi + 2} <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· 계약서 표에 행 추가 · 서명란 임대인 {oi + 2}</span></label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr 1.1fr 2fr auto', gap: 6 }}>
                      <input value={o.name} placeholder="이름" onChange={(e) => editM((mm) => { mm.s4.owners![oi + 1].name = e.target.value; })} />
                      <input value={o.rrn} placeholder="주민등록번호" onChange={(e) => editM((mm) => { mm.s4.owners![oi + 1].rrn = e.target.value; })} />
                      <input value={o.tel} placeholder="휴대폰번호" onChange={(e) => editM((mm) => { mm.s4.owners![oi + 1].tel = e.target.value; })} />
                      <input value={o.addr} placeholder="주소" onChange={(e) => editM((mm) => { mm.s4.owners![oi + 1].addr = e.target.value; })} />
                      <button className="vbtn" onClick={() => editM((mm) => { mm.s4.owners!.splice(oi + 1, 1); })}>✕</button>
                    </div>
                  </div>
                ))}
                <div className="mf" style={{ gridColumn: '1 / -1' }}>
                  <button className="vbtn" onClick={() => editM((mm) => {
                    if (!mm.s4.owners || !mm.s4.owners.length) {
                      mm.s4.owners = [{ name: mm.s4.owner || '', rrn: mm.s4.ownerRrn || '', tel: mm.s4.ownerTel || '', addr: mm.s4.ownerAddr || '' }];
                    }
                    mm.s4.owners.push({ name: '', rrn: '', tel: '', addr: '' });
                  })}>＋ 임대인 추가 (공동 소유)</button>
                </div>
                <div className="mf"><label>임대차 계약기간</label><input value={m.s4.leasePeriod || ''} onChange={(e) => editM((mm) => { mm.s4.leasePeriod = e.target.value; })} /></div>
                <div className="mf"><label>임대차계약서 작성일</label><input value={m.s4.leaseDate || ''} onChange={(e) => editM((mm) => { mm.s4.leaseDate = e.target.value; })} /></div>
                <div className="mf"><label>조합 개업일 <span style={{ color: 'var(--muted)', fontWeight: 400 }}>· 사업자등록신청서 개업일 · 미입력 시 신청일 사용</span></label><input value={m.s4.openDate || ''} onChange={(e) => editM((mm) => { mm.s4.openDate = e.target.value; })} placeholder="미입력 시 신청일 사용" /></div>
                <div className="mf"><label>조합 주소지 면적</label>
                  <div className="mfv">
                    <input value={m.s4.area || ''} onChange={(e) => editM((mm) => { mm.s4.area = e.target.value; })} placeholder="예: 84.5㎡" />
                    <button className="vbtn" onClick={() => onDocView({ free: true, docKey: '조합 주소지 등기부등본', label: '조합 주소지 면적', s4field: 'area', fileName: getS3(p).up.reg || '', s3key: getS3(p).upKey?.reg || '' })}>📄 등본 열람</button>
                  </div>
                </div>
              </div>
              {/* 사업자등록 신청서 위임장 대리인 — 기본은 지도사 본인 · 조합마다 고침(2026-10-23 지도사) */}
              <div className="mgrp">사업자등록 신청 대리인 (신청서 위임장) · 기본은 임우승 경영지도사</div>
              <div className="mgrid">
                {([['name', '대리인 성명', '비우면 대표자가 직접 신청'], ['rrn', '대리인 주민등록번호', ''], ['tel', '대리인 전화번호', '010-0000-0000'], ['rel', '신청인과의 관계', '예: 경영지도사']] as [keyof MasterAgent, string, string][]).map(([k, label, ph]) => (
                  <div className="mf" key={k}><label>{label}</label>
                    <input value={(m.s4.agent || DEFAULT_AGENT)[k] || ''} placeholder={ph}
                      onChange={(e) => editM((mm) => { mm.s4.agent = { ...(mm.s4.agent || DEFAULT_AGENT), [k]: e.target.value }; })} />
                  </div>
                ))}
                {m.s4.agent && JSON.stringify(m.s4.agent) !== JSON.stringify(DEFAULT_AGENT) && (
                  <div className="mf" style={{ alignSelf: 'end' }}>
                    <button className="vbtn" onClick={() => editM((mm) => { mm.s4.agent = { ...DEFAULT_AGENT }; })}>임우승 경영지도사로 되돌리기</button>
                  </div>
                )}
              </div>
              <div className="mgrp">LP 관계 (GP 기준)</div>
              {m.lps.length ? m.lps.map((l, i) => (
                <div className="mgrid lprow" key={i}>
                  <div className="lpidx">LP{i + 1}</div>
                  <div className="mf"><label>{(l.name || ('LP' + (i + 1)))} 관계</label>
                    <select value={l.rel || ''} onChange={(e) => editM((mm) => { mm.lps[i].rel = e.target.value; })}>
                      <option value="">관계 선택</option>
                      {RELS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
              )) : <div className="snote" style={{ padding: '4px 0' }}>LP 없음</div>}
            </>
          )}
        />
      </MasterCollapse>
      <div className="lbl" style={{ marginTop: 14 }}>구비서류 (7) · 워드·PDF 자동 생성, 3단계 최종본 연동</div>
      <S3Row no={1} title="조합결성계획 승인 공문">
        {approvalCur
          ? <OkFile name={approvalDisp} onPv={approvalKey ? () => openS3(approvalKey, approvalDisp, true).catch((e) => toast((e as Error).message)) : undefined} onDl={() => downloadUploadFile(p, approvalKey || '', approvalDisp, toast)} onRe={upApproval} />
          : <UpBtn label="승인 공문 업로드" onUp={upApproval} />}
        {/* 승인 공문은 VICS에서 내려받는다 — 13단계처럼 로그인 정보를 바로 옆에 (2026-10-25 지도사) */}
        <VicsLogin p={p} toast={toast} />
      </S3Row>
      <S3Row no={2} title="개인투자조합 표준 규약">{ref3('rule')}</S3Row>
      <S3Row no={3} title="조합 결성계획서">{ref3('plan')}</S3Row>
      <S3Row no={4} title="GP 신분증">
        {gpidFn
          ? <RefFile name={gpidFn} tag="1단계 제출" onPv={() => onFilePv(gpidFn, 'GP 신분증')} onDl={() => downloadCollectFile(p, gpidFn, toast)} />
          : <Wait msg="1단계에서 GP 신분증 미제출 · 자료수집에서 받아주세요" />}
      </S3Row>
      <S3Row no={5} title="조합원 명부">{gd('roster')}</S3Row>
      <S3Row no={6} title="무상 임대차계약서">{gd('lease')}</S3Row>
      <S3Row no={7} title="사업자등록 신청서">{gd('bizreg')}</S3Row>
      <button className="btn pri sm" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={() => { window.open('https://www.hometax.go.kr', '_blank', 'noopener'); toast('홈택스에서 개인투자조합 고유번호증을 신청하세요'); }}>
        <Icon n="send" style={{ width: 13, height: 13 }} /> 고유번호증 발급 신청
      </button>
    </div>
  );
}
